import { yupResolver } from '@hookform/resolvers/yup';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { array, boolean, date, number, object, string } from 'yup';
import { EEmployeeContract } from '../../../../shared/enums/user/EContract';
import { ECountry } from '../../../../shared/enums/user/ECountry';
import { EEmployeeType } from '../../../../shared/enums/user/EEmployeeType';
import { EGender } from '../../../../shared/enums/user/EGender';
import { ELanguage } from '../../../../shared/enums/user/ELanguage';
import { EMarital } from '../../../../shared/enums/user/EMarital';
import { EReligion } from '../../../../shared/enums/user/EReligion';
import { ERole } from '../../../../shared/enums/user/ERole';
import { ETheme } from '../../../../shared/enums/user/ETheme';
import { EWorkmode } from '../../../../shared/enums/user/EWorkmode';
import { handleError } from '../../../../shared/functions/handleError';
import { IDepartment } from '../../../../shared/interfaces/department/IDepartment';
import { IProject } from '../../../../shared/interfaces/projects/IProject';
import { ISkill, IUser } from '../../../../shared/interfaces/user/IUser';
import { AdminEmployeeService } from '../service';
import EmployeeTabs from './tabs/EmployeeTabs';

export const userSchema = object({
  // Basic Information
  email: string()
    .email('validation.invalidEmail')
    .required('validation.required'),
  firstName: string()
    .required('validation.required')
    .min(2, 'validation.minLength'),
  position: string()
    .required('validation.required')
    .min(2, 'validation.minLength'),
  lastName: string()
    .required('validation.required')
    .min(2, 'validation.minLength'),
  phoneNumber: string()
    .min(9, 'validation.minLength')
    .max(15, 'validation.maxLength')
    .matches(/^[0-9+\-\s()]*$/, 'validation.invalidPhone'),
  displayName: string(),

  // Personal Information
  birthday: date()
    .required('validation.required')
    .max(new Date(), 'validation.futureDate'),
  maritalStatus: string()
    .oneOf(Object.values(EMarital), 'validation.invalidOption')
    .required('validation.required'),
  gender: string()
    .oneOf(Object.values(EGender), 'validation.invalidOption')
    .required('validation.required'),
  religion: string()
    .oneOf(Object.values(EReligion), 'validation.invalidOption')
    .required('validation.required'),

  // Address Information
  address: string()
    .min(3, 'validation.minLength')
    .required('validation.required'),
  city: string().required('validation.required'),
  country: string()
    .oneOf(Object.values(ECountry), 'validation.invalidOption')
    .required('validation.required'),
  zipCode: string().required('validation.required'),

  // Employment Information
  employeeType: string()
    .oneOf(Object.values(EEmployeeType), 'validation.invalidOption')
    .required('validation.required'),
  workMode: string()
    .oneOf(Object.values(EWorkmode), 'validation.invalidOption')
    .required('validation.required'),
  dateOfJoin: date().required('validation.required'),
  employeeContract: string()
    .oneOf(Object.values(EEmployeeContract), 'validation.invalidOption')
    .required('validation.required'),
  contractEndDate: date()
    .nullable()
    .when('employeeContract', {
      is: EEmployeeContract.FIXED_TERM,
      then: (schema) => schema.required('validation.required'),
    }),

  nbChildren: number()
    .min(0, 'validation.minValue')
    .required('validation.required'),

  // Role and Permissions
  role: string()
    .oneOf(Object.values(ERole), 'validation.invalidOption')
    .required('validation.required'),
  isVerified: boolean(),
  canBeOfficeOrderly: boolean(),

  // Social Links
  gitHub: string().url('validation.invalidUrl'),
  linkedin: string().url('validation.invalidUrl'),
  skype: string(),

  // Preferences
  theme: string()
    .oneOf(Object.values(ETheme), 'validation.invalidOption')
    .required('validation.required'),
  language: string()
    .oneOf(Object.values(ELanguage), 'validation.invalidOption')
    .required('validation.required'),
  isEmailNotificationEnabled: boolean(),

  // leave days
  vacationDays: number()
    .min(0, 'validation.minValue')
    .required('validation.required'),
  carryOverVacationDays: number()
    .min(0, 'validation.minValue')
    .required('validation.required'),
  businessTravelLeaveDays: number()
    .min(0, 'validation.minValue')
    .required('validation.required'),
  sickDays: number()
    .min(0, 'validation.minValue')
    .required('validation.required'),

  // Relations
  departments: array().of(number().required('validation.required')),
  projects: array().of(number().required('validation.required')),
});

const EmployeeUpdate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<IUser>({
    resolver: yupResolver(userSchema) as unknown as Resolver<IUser>,
  });
  const [file, setFile] = useState<File | null>(null);
  const [files, setFiles] = useState<FileList | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const submitHandler = async (state: IUser) => {
    const formData = new FormData();
    formData.append('body', JSON.stringify(state));
    if (file) {
      formData.append('file', file);
    }
    if (files) {
      for (const file of files as unknown as []) {
        formData.append('files', file);
      }
    }
    setIsLoading(true);
    await AdminEmployeeService.update(formData, state?.id)
      .then((response) => {
        if (response) {
          navigate('/admin/employees');
          enqueueSnackbar(
            t('toasts.successfullyUpdated', { title: response?.displayName }),
            { variant: 'success' },
          );
        }
      })
      .catch(handleError)
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (id) {
      AdminEmployeeService.getSingle(Number(id))
        .then((response) => {
          reset({
            ...response,
            projects: response?.projects?.map(
              (project: IProject) => project.id,
            ),
            departments: response?.departments?.map(
              (department: IDepartment) => department.id,
            ),
            skills: response?.skills?.map((skill: ISkill) => skill.id),
          });
        })
        .catch(handleError);
    }
  }, [id, reset]);
  return (
    <form onSubmit={handleSubmit(submitHandler)}>
      <EmployeeTabs
        isLoading={isLoading}
        control={control}
        isUpdate
        errors={Object.keys(errors)}
        watch={watch}
        file={file}
        setFile={setFile}
        files={files}
        setFiles={setFiles}
      />
    </form>
  );
};

export default EmployeeUpdate;
